import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, FileDown } from "lucide-react";
import { ArabicCalendar } from "@/components/ui/arabic-calendar";
import { generateLateAttendancePDF } from "@/utils/lateAttendancePdfGenerator";
import {
  convertToArabicNumbers,
  formatHijriDateForPDF,
} from "@/utils/pdf/pdfUtils";
import { toast } from "@/hooks/use-toast";

interface AttendanceReportFiltersProps {
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  selectedStage: string;
  handleStageChange: (value: string) => void;
  selectedGrade: string;
  handleGradeChange: (value: string) => void;
  selectedSection: string;
  setSelectedSection: (value: string) => void;
  selectedStudent?: string;
  setSelectedStudent?: (value: string) => void;
  validEnabledStages: any[];
  safeGetStageDisplayName: (stage: any) => string;
  filteredAvailableGrades: string[];
  availableSectionsForGrade: string[];
  isLateReport?: boolean;
  filterTitle?: string;
  filteredStudents?: any[];
  enableStudentSelect?: boolean;
}

export const AttendanceReportFilters = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedStage,
  handleStageChange,
  selectedGrade,
  handleGradeChange,
  selectedSection,
  setSelectedSection,
  selectedStudent,
  setSelectedStudent,
  validEnabledStages,
  safeGetStageDisplayName,
  filteredAvailableGrades,
  availableSectionsForGrade,
  isLateReport = false,
  filterTitle = "فلتر تقرير الحضور",
  filteredStudents = [],
  enableStudentSelect = false,
}: AttendanceReportFiltersProps) => {
  const isGradeSelected = !!selectedGrade && selectedGrade !== "all";
  const isSectionSelected = !!selectedSection && selectedSection !== "all";
  const isStudentSelectable =
    !enableStudentSelect ||
    (isGradeSelected && isSectionSelected && filteredStudents.length > 0);

  return (
    <Card className="mb-6 bg-gradient-to-br from-secondary/5 to-secondary/10 backdrop-blur-sm border-secondary/20 shadow-md">
      <div className="bg-secondary text-white rounded-t-lg p-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-white" />
          <h3 className="text-lg font-semibold text-white">{filterTitle}</h3>
        </div>
      </div>
      <CardContent className="p-6 pt-4">
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm font-semibold text-blue-900 mb-2">
            كيفية عرض التقرير
          </p>
          <p className="text-sm text-blue-800">
            1) اختر الصف والشعبة، 2) اختر الطالب، 3) اختر تاريخ البداية، 4) يظهر
            التقرير ويمكنك تنزيله PDF.
          </p>
        </div>

        <div className="space-y-4">
          {/* صف أول: المرحلة والصف والشعبة */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* المرحلة */}
            {validEnabledStages.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-primary-blue mb-2">
                  المرحلة
                </label>
                <Select
                  value={selectedStage || "all"}
                  onValueChange={handleStageChange}
                >
                  <SelectTrigger className="bg-white/70 border-blue-200">
                    <SelectValue placeholder="جميع المراحل" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع المراحل</SelectItem>
                    {validEnabledStages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id}>
                        {safeGetStageDisplayName(stage)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* الصف */}
            <div>
              <label className="block text-sm font-medium text-primary-blue mb-2">
                الصف
              </label>
              <Select
                value={selectedGrade || "all"}
                onValueChange={handleGradeChange}
                disabled={
                  validEnabledStages.length > 1 &&
                  (!selectedStage || selectedStage == "all")
                }
              >
                <SelectTrigger className="bg-white/70 border-blue-200">
                  <SelectValue placeholder="جميع الصفوف" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الصفوف</SelectItem>
                  {filteredAvailableGrades.map((grade) => (
                    <SelectItem key={grade} value={grade}>
                      {grade}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* الشعبة */}
            <div>
              <label className="block text-sm font-medium text-primary-blue mb-2">
                الشعبة
              </label>
              <Select
                value={selectedSection || "all"}
                onValueChange={setSelectedSection}
                disabled={!selectedGrade || selectedGrade == "all"}
              >
                <SelectTrigger className="bg-white/70 border-blue-200">
                  <SelectValue placeholder="جميع الشعب" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الشعب</SelectItem>
                  {availableSectionsForGrade.map((section) => (
                    <SelectItem key={section} value={section}>
                      {section}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {enableStudentSelect && (
              <div className="space-y-2">
                <label className="text-sm font-medium">الطالب</label>
                <Select
                  value={selectedStudent || "all"}
                  onValueChange={(value) =>
                    setSelectedStudent?.(value === "all" ? "" : value)
                  }
                  disabled={!isStudentSelectable}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        isStudentSelectable
                          ? "اختر الطالب"
                          : "اختر الصف والشعبة أولاً"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">اختر طالباً</SelectItem>
                    {filteredStudents.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* تحذير عند عدم اختيار التواريخ */}
          {!startDate && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 font-medium">
                يجب تحديد تاريخ البداية لعرض التقرير
              </p>
            </div>
          )}

          {/* صف ثاني: تواريخ البداية والنهاية */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* تاريخ البداية */}
            <div>
              <label className="block text-sm font-medium text-primary-blue mb-2">
                تاريخ البداية <span className="text-red-500">*</span>
              </label>
              <ArabicCalendar
                value={startDate}
                onChange={setStartDate}
                placeholder="اختر تاريخ البداية"
                className={`bg-white/70 ${
                  !startDate ? "border-red-300" : "border-blue-200"
                }`}
              />
            </div>

            {/* تاريخ النهاية */}
            <div>
              <label className="block text-sm font-medium text-primary-blue mb-2">
                تاريخ النهاية
              </label>
              <ArabicCalendar
                value={endDate}
                onChange={setEndDate}
                placeholder="اختر تاريخ النهاية"
                className="bg-white/70 border-blue-200"
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
